import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from 'react-query';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

const LocationForm = ({ location, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    country: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name || '',
        region: location.region || '',
        country: location.country || '',
        description: location.description || ''
      });
    }
  }, [location]);

  const createLocationMutation = useMutation(
    async (data) => {
      return await api.post('/api/v1/admin/locations', data);
    },
    {
      onSuccess: () => {
        toast.success('Location created successfully');
        queryClient.invalidateQueries('locations');
        if (onSubmit) onSubmit();
      },
      onError: (error) => {
        console.error('Error creating location:', error);
        
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          toast.error('Failed to create location. Please try again.');
        }
        
        setIsSubmitting(false);
      }
    }
  );

  const updateLocationMutation = useMutation(
    async (data) => {
      return await api.put(`/api/v1/admin/locations/${location.id}`, data);
    },
    {
      onSuccess: () => {
        toast.success('Location updated successfully');
        queryClient.invalidateQueries('locations');
        if (onSubmit) onSubmit();
      },
      onError: (error) => {
        console.error('Error updating location:', error);
        
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          toast.error('Failed to update location. Please try again.');
        }
        
        setIsSubmitting(false);
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Location name is required';
    }
    
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    if (location) {
      updateLocationMutation.mutate(formData);
    } else {
      createLocationMutation.mutate(formData);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {location ? 'Edit Location' : 'Add New Location'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {location 
                ? 'Update the details for this location.' 
                : 'Create a new location that can be used when creating tours.'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Location Name*
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`block w-full rounded-md sm:text-sm ${
                    errors.name 
                      ? 'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-primary focus:border-primary'
                  }`}
                  placeholder="e.g. Mombasa Beach"
                />
                {errors.name && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div>
                )}
              </div>
              {errors.name && (
                <p className="mt-2 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="region" className="block text-sm font-medium text-gray-700">
                Region
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  name="region"
                  id="region"
                  value={formData.region}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="e.g. Coast Region"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                Country*
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  name="country"
                  id="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`block w-full rounded-md sm:text-sm ${
                    errors.country 
                      ? 'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-primary focus:border-primary'
                  }`}
                  placeholder="e.g. Kenya"
                />
                {errors.country && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                  </div>
                )}
              </div>
              {errors.country && (
                <p className="mt-2 text-sm text-red-600">{errors.country}</p>
              )}
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1">
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  placeholder="Brief description of the location"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Provide a brief description of this location to help tour creators.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {location ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              location ? 'Update Location' : 'Create Location'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

LocationForm.propTypes = {
  location: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default LocationForm;
